import { Content, Course, GiftWithMedia, ReceivedGift } from "JS/Models";
import sortableHeader from "JS/React/HOC/SortableHeader";
import {
    ColumnSort,
    getColumnSort,
    sortData,
    Sorting,
    sortViaAcquiredDate,
    sortViaPlayedDate,
} from "JS/Types";
import { useGetFavorites } from "JS/React/Hooks/Favorites";
import { AppTypography } from "JS/React/Components/AppTypography";
import { RenderIf } from "JS/React/Components/Wrapper/RenderIf";
import { messaging } from "JS/Helpers/UserMessaging";
import { styled } from "@mui/material/styles";
import { Box, InternalStandardProps } from "@mui/material";
import {
    createRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { VideoCategoryTitle } from "../Videos";
import moment from "moment";
import { GiftNavStack, INavStack } from "JS/Models/navStack";
import AutoSizer from "react-virtualized-auto-sizer";
import qs from "qs";
import { FixedSizeList, ListOnItemsRenderedProps } from "react-window";
import { useNavigate, useLocation } from "react-router-dom";
import { CONTENT_TILE_SIZE, nidControlTypes } from "JS/Helpers/Constants";
import { AppContentRow } from "JS/React/Components/AppContentRow";
import { AudioCategoryTitle } from ".";
import { PlaylistAudioContent } from "../DownloadsOffline/Playlist/PlaylistDetail";
import { useAppSelector } from "JS/Redux/Store";
import { AppContentListItemType } from "JS/React/Components/AppContentListItem";
import { useDebouncedFetchThumbnails } from "JS/React/Hooks/Common/ImageUpload";
export interface VideoDownloadProps {
    nid: string;
    isDownloadInProgress: boolean;
    isInDownloadQueue: boolean;
    isDownloaded: Promise<boolean>;
    inProgressDownloaded: boolean;
}
export interface AudioDownloadProps {
    nid: string;
    isDownloading: boolean;
    inProgressDownloaded: boolean;
    isInDownloadedQueue: boolean;
    isAudioDownloaded: Promise<boolean>;
}
export interface ListProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {
    listContent: (Content | ReceivedGift | Course)[];
    searchQuery?: string;
    categoryId?: string;
    isChecked?: boolean;
    showSortHeader?: boolean;
    selectedPlaylistContent?: string[];
    setGlobalStack?: (
        payload: INavStack,
    ) => void | ((payload: GiftNavStack) => void);
    globalStack?: INavStack;
    favoritesContent?: string[];
    handleListItemClick?: (
        filteredVideo?: Content | ReceivedGift | Course,
        isAutoPlay?: boolean | string,
        scrollIndex?: number,
    ) => void;
    getDownloadHelperData?: (
        param: (Content | ReceivedGift)[],
    ) => (nid: string) => AudioDownloadProps | VideoDownloadProps;
    controlType?: string;
    isResumeable?: (filteredVideo: Content | ReceivedGift) => boolean;
    selectMode?: boolean;
    selectedItems?: string[];
    onGiftCheckChanged?: (nid: string, checked: boolean) => void;
    shouldSort?: boolean;
    checkIsCrsDownloaded?: (content: Course | ReceivedGift) => Promise<boolean>;
    listHeight?: string;
    inProgressNid?: string;
    contentType?: AppContentListItemType;
    parentSort?: Sorting;
}

const StyledRoot = styled("div")({});

const StyledEmptyList = styled(AppTypography)(({ theme }) => ({
    marginTop: theme.spacing(3),
    color: theme.palette.grey[500],
}));

interface ListHeightBoxProps {
    listHeight?: string;
    inProgressNid?: string;
}

const StyledListHeightBox = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== "listHeight" && prop !== "inProgressNid",
})<ListHeightBoxProps>(({ theme, listHeight, inProgressNid }) => ({
    // Default fallback
    height: "calc(var(--vh, 1vh) * 85)",
    [theme.breakpoints.down("xl")]: {
        height: "calc(var(--vh, 1vh) * 90)",
        "@supports (height: 90dvh)": {
            height: "90dvh",
        },
    },
    [theme.breakpoints.down("lg")]: {
        height: "calc(var(--vh, 1vh) * 90)",
    },
    [theme.breakpoints.down("md")]: {
        height: "calc(var(--vh, 1vh) * 70)",
    },
    [theme.breakpoints.down("sm")]: {
        height:
            listHeight ||
            (!!inProgressNid
                ? "calc(var(--vh, 1vh) * 65)"
                : "calc(var(--vh, 1vh) * 70)"),
    },
}));

const SortableHeader = sortableHeader<HTMLSpanElement>("span");
export const ListingComponent = (props: ListProps) => {
    const inProgressNid = useAppSelector(
        (state) => state.download.inProgressNid,
    );
    const {
        className,
        listContent: content = [],
        categoryId,
        searchQuery,
        setGlobalStack,
        globalStack,
        favoritesContent,
        handleListItemClick,
        getDownloadHelperData,
        controlType,
        showSortHeader,
        isResumeable,
        selectMode = false,
        selectedItems = [],
        onGiftCheckChanged,
        shouldSort = true,
        checkIsCrsDownloaded,
        contentType,
        parentSort,
        listHeight,
        ...rest
    } = props;
    const navigate = useNavigate();
    const location = useLocation();
    useGetFavorites(controlType === "course" || !!favoritesContent?.length);
    const sortingHandler = () => {
        if (controlType === "video") {
            return categoryId === VideoCategoryTitle.RECENTLY_PLAYED;
        } else if (controlType === "audio") {
            return categoryId === AudioCategoryTitle.RECENTLY_PLAYED;
        }
    };
    const [sorting, setSorting] = useState<Sorting>(
        sortingHandler()
            ? [
                  {
                      col: "played",
                      dir: "desc",
                      position: 0,
                  },
              ]
            : globalStack?.sorting,
    );
    const { thumbnails, refetch: fetchThumbnails } =
        useDebouncedFetchThumbnails();

    const onSortChange = useCallback(
        (columnSort: ColumnSort, oldOrder: number) => {
            setSorting([columnSort]);
            setGlobalStack({ sorting: [columnSort] });
        },
        [setGlobalStack],
    );
    const finalData = useMemo(() => {
        if (!sorting) return content;
        if (
            shouldSort &&
            categoryId &&
            (controlType.includes("video") || controlType.includes("audio"))
        ) {
            if (sorting[0].col === "acquired_date") {
                const newContent = content.map((d: Content | GiftWithMedia) => {
                    if ("sender" in d) {
                        if (d.sender.sending_date_time) {
                            const dateToFormat = isNaN(
                                +d.sender.sending_date_time,
                            )
                                ? d.sender.sending_date_time
                                : +d.sender.sending_date_time;
                            const timeStamp = moment(dateToFormat)
                                .valueOf()
                                .toString();
                            return {
                                ...d,
                                acquired_date: timeStamp,
                            };
                        } else {
                            return {
                                ...d,
                                acquired_date: null,
                            };
                        }
                    } else {
                        return d;
                    }
                });
                return sortViaAcquiredDate(newContent, sorting);
            }
            if (sorting[0].col === "played")
                return sortViaPlayedDate(
                    content as (Content | ReceivedGift)[],
                    sorting,
                );
            return sortData(content, sorting);
        } else return content;
    }, [categoryId, content, controlType, shouldSort, sorting]);

    const { visibleIndices, onRender } = useItemsRendered();

    const visibleData = useMemo(() => {
        if (!finalData || !visibleIndices) return [];
        return finalData.slice(visibleIndices.start, visibleIndices.end);
    }, [finalData, visibleIndices]);

    function isCorrectTypeForControlType(
        data: any,
    ): data is (Content | ReceivedGift)[] {
        return data.every(
            (item: Content | ReceivedGift) =>
                controlType === "video" ||
                controlType === "audio" ||
                controlType === "course",
        );
    }
    let getDownloadedStatusById;
    if (isCorrectTypeForControlType(visibleData)) {
        getDownloadedStatusById = getDownloadHelperData
            ? getDownloadHelperData(visibleData)
            : false;
    }
    useEffect(() => {
        if (
            !finalData ||
            !Array.isArray(finalData) ||
            finalData.length === 0 ||
            !visibleIndices ||
            !thumbnails
        ) {
            return;
        }
        const thumbnailNids = Object.keys(thumbnails);
        const visibleContent = finalData.slice(
            visibleIndices.start,
            visibleIndices.end,
        );

        fetchThumbnails(
            visibleContent.filter((v) => !thumbnailNids.includes(v.nid)),
        );
    }, [finalData, visibleIndices]);

    const isFavouriteContent = (filteredContent: Content | ReceivedGift) =>
        favoritesContent?.includes(filteredContent.nid);

    const courseContentType = (content: Course | ReceivedGift) => {
        return (content as Course)?.publish_free === "1" ||
            (content as Course)?.is_purchased ||
            content.isReceived
            ? "courses"
            : "purchasableCourses";
    };
    const getDownloadStatus = (
        content: Content | ReceivedGift,
    ): VideoDownloadProps | AudioDownloadProps => {
        return getDownloadedStatusById(content?.nid);
    };
    const { scrollTo = "0" } = useQueryParams();
    const listRef = createRef<FixedSizeList>();
    const scrollControlRef = useRef<boolean>(false);
    const searchScrollRef = useRef<boolean>(false);

    useEffect(() => {
        if (+scrollTo >= 0 && listRef.current && !scrollControlRef.current) {
            let scrollToElem = +scrollTo;
            if (nidControlTypes.includes(controlType)) {
                scrollToElem = getIndexByNid(scrollTo as string);
            }
            listRef.current.scrollToItem(scrollToElem, "center");
            scrollControlRef.current = true;
        }
    }, [scrollTo, finalData, listRef]);
    useEffect(() => {
        if (listRef.current && searchQuery && searchScrollRef.current) {
            resetScrollings();
        }
        searchScrollRef.current = true;
    }, [searchQuery]);

    useEffect(() => {
        const changedOccured = sorting ?? parentSort;
        if (listRef.current && changedOccured) {
            const searchParams = new URLSearchParams(location.search);
            let scrollNid = 0;
            if (nidControlTypes.includes(controlType)) {
                scrollNid = +finalData[0].nid;
            }
            resetScrollings();

            searchParams.set("scrollTo", scrollNid.toString());
            navigate(
                {
                    pathname: location.pathname,
                    search: searchParams.toString(),
                },
                { replace: true }
            );
        }
    }, [sorting, parentSort]);

    const resetScrollings = () => {
        listRef?.current?.scrollToItem(0, "start");
    };
    const getIndexByNid = (nid: string) => {
        return finalData.findIndex((elem) => elem.nid === nid);
    };

    return (
        <StyledRoot className={className} {...rest}>
            {!!showSortHeader && (
                <Box display={"flex"} justifyContent={"space-around"}>
                    <SortableHeader
                        upDirection="asc"
                        downDirection="desc"
                        sort={getColumnSort(sorting, {
                            col: "title",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>A-Z</span>
                    </SortableHeader>
                    <SortableHeader
                        upDirection="desc"
                        downDirection="asc"
                        sort={getColumnSort(sorting, {
                            col: "release_date",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>Released</span>
                    </SortableHeader>
                    <SortableHeader
                        upDirection="desc"
                        downDirection="asc"
                        sort={getColumnSort(sorting, {
                            col: "acquired_date",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>Acquired</span>
                    </SortableHeader>
                    <SortableHeader
                        upDirection="desc"
                        downDirection="asc"
                        sort={getColumnSort(sorting, {
                            col: "played",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>Played</span>
                    </SortableHeader>
                </Box>
            )}
            <RenderIf
                isTrue={!!finalData?.length}
                fallback={
                    <StyledEmptyList align="center" variant="h6">
                        {messaging.common.noItemsFound}
                    </StyledEmptyList>
                }
            >
                <StyledListHeightBox
                    listHeight={listHeight}
                    inProgressNid={inProgressNid}
                >
                    <AutoSizer>
                        {({ height, width }) => (
                            <FixedSizeList
                                height={height}
                                itemCount={finalData?.length || 0}
                                itemSize={CONTENT_TILE_SIZE}
                                width={width}
                                ref={listRef}
                                onItemsRendered={onRender}
                            >
                                {({ index, style }) => (
                                    <AppContentRow
                                        index={index}
                                        style={style}
                                        listData={finalData[index]}
                                        sorting={parentSort || sorting}
                                        thumbnails={thumbnails}
                                        controlType={controlType}
                                        isResumeable={isResumeable}
                                        isFavouriteContent={isFavouriteContent}
                                        {...((controlType === "audio" ||
                                            controlType === "video") && {
                                            getDownloadStatus,
                                        })}
                                        handleListItemClick={
                                            handleListItemClick
                                        }
                                        onGiftCheckChanged={onGiftCheckChanged}
                                        selectMode={selectMode}
                                        isSelected={selectedItems.includes(
                                            finalData[index].nid,
                                        )}
                                        categoryId={categoryId}
                                        contentType={contentType}
                                        {...(controlType === "course"
                                            ? {
                                                  type: courseContentType,
                                                  checkIsCrsDownloaded,
                                              }
                                            : {})}
                                        {...(controlType === "offlinePlaylist"
                                            ? {
                                                  isAllowClick: (
                                                      finalData[
                                                          index
                                                      ] as PlaylistAudioContent
                                                  ).isDownloaded,
                                                  isDisabled: !(
                                                      finalData[
                                                          index
                                                      ] as PlaylistAudioContent
                                                  ).isDownloaded,
                                              }
                                            : {})}
                                    />
                                )}
                            </FixedSizeList>
                        )}
                    </AutoSizer>
                </StyledListHeightBox>
            </RenderIf>
        </StyledRoot>
    );
};

const useItemsRendered = () => {
    const [visibleIndices, setVisibleIndices] = useState<{
        start: number;
        end: number;
    }>();

    const onRender = (props: ListOnItemsRenderedProps) => {
        setVisibleIndices({
            start: props.visibleStartIndex,
            end: props.visibleStopIndex + 1,
        });
    };

    return {
        visibleIndices,
        onRender,
    };
};

export const useQueryParams = () => {
    const location = useLocation();
    return useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });
        const { scrollTo } = parsedQueryString;
        return {
            scrollTo,
        };
    }, [location?.search]);
};
